import { Document } from "mongoose"
import { PlaidApi } from "plaid"
import { UploadedDoc } from "./document-types"
import { ProjectType, SLoanApplication } from "./loan-application"
import { StreetAddress } from "./street-address"
import { IProduct } from "./product"
import { BannedInstaller } from "./installer"
import { AirtableWebhook } from "./airtable-webhooks"

export enum ConsumerApplicationIntegration {
  Abrigo = "Abrigo",
  DecisionPro = "DecisionPro",
}

export const IntegrationLabels = {
  [ConsumerApplicationIntegration.Abrigo]: 'Abrigo',
  [ConsumerApplicationIntegration.DecisionPro]: 'Airtable + DecisionPro',
}

export type TenantSiteType = 'consumer' | 'installer'

export type TenantInstallerTerms = {
  url: string
  version: string
  createdDate: Date
}

export type ITenantClientData = {
  _id: string
  logo: UploadedDoc
  bankEmail: string
  demoLocalFinancingPartnerLogo?: UploadedDoc
  demoLocalFinancingPartnerName?: string
  name: string
  site?: TenantSiteType
  rateEffectiveDate: string
  plaidDisabled?: boolean
  personaDisabled?: boolean
  products: IProduct[]
  bannedInstallers: BannedInstaller[]
  trainingPageUrl?: string
  vendorAchFormUrl?: string
  termsAndConditionsUrl?: string
  privacyPolicyUrl?: string
  esignAgreementUrl?: string
  referralUrlCommercial?: string
  currentInstallerTerms?: TenantInstallerTerms | null
  roofCostCap?: number
  storageCostCap?: number
  batteryCostCap?: number
  hvacCostCap?: number
  advisory?: {
    message: string
    level: 'info' | 'warning' | 'danger'
  }
}

export type DocusignConfig = {
  apiAccountId?: string
  host?: string
  appUrl?: string
  integrationKey?: string
  userId?: string
  loanDocsTemplateId?: string
  rsaKeyName?: string
  loanModificationTemplateId?: string
  ownersReleaseTemplateId?: string
} & { toObject: () => DocusignConfig }

export type EquifaxPrequalConfig = {
  clientId?: string
  clientSecret?: string
  memberNo?: string
  securityCode?: string
  baseUrl?: string
  modelNo?: string
}

export type EquifaxDitConfig = {
  clientId?: string
  clientSecret?: string
  tenantId?: string
  applicationId?: string
  configId?: string
  entityId?: string
  baseUrl?: string
}

export type TenantEquifaxConfig = {
  prequal: EquifaxPrequalConfig,
  dit: EquifaxDitConfig
}

export type TenantPlaidConfig = {
  clientId?: string
  secret?: string
  env?: string
} & { toObject: () => TenantPlaidConfig }

export type TenantPersonaConfig = {
  apiKey?: string
  templateId?: string
  webhookSecret?: string
  apiVersion?: string
  baseUrl?: string
} & { toObject: () => TenantPersonaConfig }

export const DEFAULT_SOLAR_LENDER_NAMES: string[] = [
  'Goodleap',
  'Service Finance',
  'Service Finance Comp',
  'Mosaic',
  'Enfin',
  'Sunlight Financial',
  'Dividend',
  'Solar Servicing LLC',
  'SunStrong Management',
]

export function getSolarLenderNames(names?: string[] | null): string[] {
  if (names == null) {
    return [...DEFAULT_SOLAR_LENDER_NAMES]
  }
  return names
}

export type ITenant = ITenantClientData & {
  agentsBaseUrl: string
  installerHostname: string
  consumerHostname: string
  altDomains: string[]
  approvedAdders: string[]
  solarLenderNames: string[]
  referralUrl?: string
  referralUrlCommercial?: string
  dbName: string
  installerSupportEmail: string
  bankPhone: string
  bankNotifyAppSubmittedEmail?: string
  zendeskToEmail: string
  primaryAddress: StreetAddress
  docUploadFromEmail: string
  docUploadWebhookUrl: string
  docUploadWebhookSecret: string
  multipleDocsUploadWebhookUrl: string,
  multipleDocsUploadWebhookSecret: string,
  adverseActionWebhookUrl: string
  docusign: DocusignConfig
  enableResidential: boolean
  enableCommercial: boolean
  enableNonProfit: boolean
  consumerApplicationIntegration: ConsumerApplicationIntegration
  abrigoClientId?: string
  abrigoClientSecret?: string
  abrigoEnv?: string
  abrigoProductId?: string
  abrigoSolarWorkflowTemplateId?: string
  abrigoMarkStepCompleteOnDocUpload?: boolean
  decisionProSubmitUrl?: string
  decisionProProductName?: string
  airtableAccessToken?: string
  airtableWebhooks?: AirtableWebhook[]
  achOdfiRoutingNo: string
  achOdfiName: string
  achOdfiAccountNo: string
  achOdfiAccountType: string
  letterheadLogo?: UploadedDoc
  marketingDomain?: string
  products: IProduct[]
  bannedInstallers: BannedInstaller[]
  equifax?: TenantEquifaxConfig
  plaid?: TenantPlaidConfig
  persona?: TenantPersonaConfig
  installerTerms: TenantInstallerTerms[]
  getOrigin: (s: string) => string
  getConsumerOrigin: () => string
  getInstallerOrigin: () => string
  getPlaidClient: () => PlaidApi
  getCurrentInstallerTerms: () => TenantInstallerTerms | null
  getReferralUrl: (projectType?: ProjectType) => string
  hasValidACHSettings: () => boolean
  eligibleProducts: (lapp: SLoanApplication) => IProduct[]
  clientData: () => ITenantClientData
  toMaskedObject: () => ITenant
  isPersonaDisabled: () => boolean
  defaultFileStorageContainerName: () => string
  addProductFields: () => IProduct
  isEmailBanned: (email: string) => boolean
} & Document

export const initialTenantProduct = {
  productType: '',
  term: 0,
  interestOnlyPeriod: 0,
  apr: 0,
  autoPayAPR: 0,
  interestRateWithAutopay: 0,
  interestRateWithoutAutopay: 0,
  paymentFactorWithAutopay: 0,
  paymentFactorWithoutAutopay: 0,
  dealerFee: 0,
  maxPpw: 0,
  maxLoanAmount: 0,
  minLoanAmount: 0,
  disclosures: '',
  approvedStates: [],
  productDescription: '',
  detailedProductDescriptionClientHtml: '',
  detailedProductDescriptionInstallerHtml: '',
  equipmentTypes: [],
  isValidForIncentive: false,
}

export const ALLOWED_TENANT_UPDATE_FIELDS = [
  'installerHostname',
  'consumerHostname',
  'altDomains',
  'name',
  'marketingDomain',
  'referralUrl',
  'referralUrlCommercial',
  'enableResidential',
  'enableCommercial',
  'enableNonProfit',
  'rateEffectiveDate',
  'approvedAdders',
  'solarLenderNames',
  'logo',
  'letterheadLogo',
  'demoLocalFinancingPartnerLogo',
  'demoLocalFinancingPartnerName',
  'installerSupportEmail',
  'bankEmail',
  'bankNotifyAppSubmittedEmail',
  'bankPhone',
  'zendeskToEmail',
  'primaryAddress',
  'termsAndConditionsUrl',
  'privacyPolicyUrl',
  'vendorAchFormUrl',
  'esignAgreementUrl',
  'trainingPageUrl',
  'adverseActionWebhookUrl',
  'docUploadFromEmail',
  'docUploadWebhookUrl',
  'docUploadWebhookSecret',
  'multipleDocsUploadWebhookUrl',
  'multipleDocsUploadWebhookSecret',
  'decisionProSubmitUrl',
  'decisionProProductName',
  'consumerApplicationIntegration',
  'airtableAccessToken',
  'abrigoClientId',
  'abrigoClientSecret',
  'abrigoEnv',
  'abrigoSolarWorkflowTemplateId',
  'abrigoProductId',
  'abrigoMarkStepCompleteOnDocUpload',
  'achOdfiRoutingNo',
  'achOdfiName',
  'achOdfiAccountNo',
  'achOdfiAccountType',
  'installerTerms',
  'roofCostCap',
  'storageCostCap',
  'batteryCostCap',
  'hvacCostCap',
] as const

export const ALLOWED_EQUIFAX_PREQUAL_FIELDS = [
  'clientId',
  'clientSecret',
  'memberNo',
  'securityCode',
  'baseUrl',
  'modelNo',
] as const

export const ALLOWED_EQUIFAX_DIT_FIELDS = [
  'clientId',
  'clientSecret',
  'tenantId',
  'applicationId',
  'configId',
  'entityId',
  'baseUrl',
] as const

export const ALLOWED_PLAID_FIELDS = [
  'clientId',
  'secret',
] as const

export const ALLOWED_PERSONA_FIELDS = [
  'apiKey',
  'templateId',
  'webhookSecret',
  'apiVersion',
] as const

export const ALLOWED_DOCUSIGN_FIELDS = [
  'apiAccountId',
  'host',
  'appUrl',
  'integrationKey',
  'userId',
  'loanDocsTemplateId',
  'rsaKeyName',
  'loanModificationTemplateId',
  'ownersReleaseTemplateId',
] as const

const COST_CAP_FIELDS = ['roofCostCap', 'storageCostCap', 'batteryCostCap', 'hvacCostCap'] as const

export function sanitizeSolarLenderNamesPatch(patch: Record<string, any>): string | null {
  if (!('solarLenderNames' in patch)) return null
  if (!Array.isArray(patch.solarLenderNames)) {
    return 'solarLenderNames must be an array of strings'
  }

  const names: string[] = []
  for (const name of patch.solarLenderNames) {
    if (typeof name !== 'string') {
      return 'solarLenderNames must be an array of strings'
    }
    const trimmed = name.trim()
    if (trimmed) names.push(trimmed)
  }
  patch.solarLenderNames = names
  return null
}

export function validateCostCapPatch(patch: Record<string, any>): string | null {
  for (const field of COST_CAP_FIELDS) {
    if (field in patch) {
      // Treat null or empty string as "remove the cap"
      if (patch[field] === null || patch[field] === '') {
        patch[field] = undefined
        continue
      }

      const raw = patch[field]
      // Reject values Number() would silently coerce (true→1, false/[]/" "→0)
      if (
        typeof raw === 'boolean' ||
        Array.isArray(raw) ||
        (typeof raw === 'string' && raw.trim() === '')
      ) {
        return field
      }

      const val = Number(raw)
      if (!Number.isFinite(val) || val < 0) return field
      patch[field] = val
    }
  }
  return null
}

export const pickAllowedFields = <T extends readonly string[]> (
  source: Record<string, any> | undefined,
  allowedFields: T
): Record<string, any> => {
  if (!source) return {}
  const result: Record<string, any> = {}
  for (const field of allowedFields) {
    if (source[field] !== undefined) {
      result[field] = source[field]
    }
  }
  return result
}